import React, { FC, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Area, Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import StableResponsiveContainer from '../general/stable-responsive-container/stable-responsive-container.component';
import { StatsResponse } from '../../models/stats.models';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import { getSportLayerColor } from '../../pages/training-log/training-log.helpers';
import {
  buildChartData,
  CORE_LAYER_CONFIG,
  CoreLayerKey,
  filterWeeksByTimeRange,
  formatChartHoursTick,
  getCumulativeAxisDomain,
  getDistanceAxisDomain,
  getDurationAxisDomain,
  getPeriodSplitLabel,
  getSportKeysFromBreakdown,
  TimeRangeFilter,
} from './season-overview-chart.helpers';

interface SeasonOverviewChartProps {
  stats: StatsResponse | null;
  loading: boolean;
}

const DEFAULT_ACTIVE_LAYERS: CoreLayerKey[] = ['volume'];

const SeasonOverviewChart: FC<SeasonOverviewChartProps> = ({ stats, loading }) => {
  const [timeRange, setTimeRange] = useState<TimeRangeFilter>('all');
  const [activeLayers, setActiveLayers] = useState<Set<string>>(new Set(DEFAULT_ACTIVE_LAYERS));

  const weeks = stats?.weekBreakdown ?? [];
  const periodCount = stats?.periodBreakdown?.length ?? 0;
  const periodSplit = getPeriodSplitLabel(periodCount);

  const filteredWeeks = useMemo(() => filterWeeksByTimeRange(weeks, timeRange), [weeks, timeRange]);

  const sportKeys = useMemo(() => getSportKeysFromBreakdown(filteredWeeks), [filteredWeeks]);

  const chartData = useMemo(() => {
    const rows = buildChartData(filteredWeeks, sportKeys);
    return rows.map((row, index) => ({
      ...row,
      weekLabel: dayjs(row.week).format('MMM D'),
      weekIndex: index + 1,
    }));
  }, [filteredWeeks, sportKeys]);

  const showDistance = activeLayers.has('weeklyDistance');
  const showVolume = activeLayers.has('volume');
  const showCumulative = activeLayers.has('cumulative');
  const activeSportKeys = sportKeys.filter((key) => activeLayers.has(key));
  const showDurationAxis = showVolume || activeSportKeys.length > 0;

  const distanceDomain = useMemo(() => getDistanceAxisDomain(chartData.map((row) => row.weeklyDistance)), [chartData]);

  const durationDomain = useMemo(() => {
    const durationValues = chartData.flatMap((row) => [
      ...(showVolume ? [row.volume] : []),
      ...activeSportKeys.map((key) => Number(row[key] ?? 0)),
    ]);
    return getDurationAxisDomain(durationValues);
  }, [chartData, showVolume, activeSportKeys]);

  const cumulativeDomain = useMemo(() => getCumulativeAxisDomain(chartData.map((row) => row.cumulative)), [chartData]);

  const gridAxisId = showDurationAxis ? 'duration' : showDistance ? 'distance' : 'cumulative';
  const distanceAxisWidth = 48;
  const durationAxisWidth = 48;
  const cumulativeAxisWidth = 52;
  const leftMargin = (showDistance ? distanceAxisWidth : 0) + (showDurationAxis ? durationAxisWidth : 8);
  const rightMargin = showCumulative ? cumulativeAxisWidth : 16;

  const toggleLayer = (layerKey: string) => {
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerKey)) {
        next.delete(layerKey);
      } else {
        next.add(layerKey);
      }
      return next;
    });
  };

  const distanceUnit = getMeasurementUnit();

  const formatTooltipValue = (layerKey: string, value: number) => {
    if (layerKey === 'weeklyDistance') {
      return `${value.toFixed(1)} ${distanceUnit}`;
    }
    if (layerKey === 'cumulative') {
      return formatChartHoursTick(value);
    }
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')} hrs`;
  };

  const getLayerKeyFromName = (name: string): string => {
    const coreKey = (Object.keys(CORE_LAYER_CONFIG) as CoreLayerKey[]).find(
      (key) => CORE_LAYER_CONFIG[key].label === name
    );
    if (coreKey) {
      return coreKey;
    }
    const sportKey = sportKeys.find((key) => key === name);
    if (sportKey) {
      return sportKey;
    }
    return 'volume';
  };

  const renderSeries = () => {
    const series: React.ReactElement[] = [];

    if (showDistance) {
      series.push(
        <Bar
          key="weeklyDistance"
          yAxisId="distance"
          dataKey="weeklyDistance"
          name={CORE_LAYER_CONFIG.weeklyDistance.label}
          fill={CORE_LAYER_CONFIG.weeklyDistance.color}
          fillOpacity={0.75}
          radius={[3, 3, 0, 0]}
          maxBarSize={28}
          isAnimationActive={false}
        />
      );
    }

    if (showVolume) {
      series.push(
        <Area
          key="volume"
          yAxisId="duration"
          type="monotone"
          dataKey="volume"
          name={CORE_LAYER_CONFIG.volume.label}
          stroke={CORE_LAYER_CONFIG.volume.color}
          fill={CORE_LAYER_CONFIG.volume.color}
          fillOpacity={0.08}
          strokeWidth={2}
          dot={{ r: 3, fill: CORE_LAYER_CONFIG.volume.color }}
          activeDot={{ r: 5 }}
          isAnimationActive={false}
        />
      );
    }

    if (showCumulative) {
      series.push(
        <Line
          key="cumulative"
          yAxisId="cumulative"
          type="monotone"
          dataKey="cumulative"
          name={CORE_LAYER_CONFIG.cumulative.label}
          stroke={CORE_LAYER_CONFIG.cumulative.color}
          strokeWidth={2}
          strokeDasharray="6 4"
          dot={false}
          activeDot={{ r: 4 }}
          isAnimationActive={false}
        />
      );
    }

    activeSportKeys.forEach((sportKey, index) => {
      const color = getSportLayerColor(sportKey, index);
      series.push(
        <Line
          key={sportKey}
          yAxisId="duration"
          type="monotone"
          dataKey={sportKey}
          name={sportKey}
          stroke={color}
          strokeWidth={2}
          dot={{ r: 3, fill: color }}
          activeDot={{ r: 5 }}
          isAnimationActive={false}
        />
      );
    });

    return series;
  };

  return (
    <div className="hero-chart-card">
      <div className="hero-chart-header">
        <div>
          <div className="hero-chart-title">Season Overview</div>
          <div className="hero-chart-subtitle">
            Toggle layers to compare volume, zones, sport modes, and more over time
          </div>
        </div>
        <div className="time-range">
          <button type="button" className={timeRange === 'all' ? 'active' : ''} onClick={() => setTimeRange('all')}>
            Full Season
          </button>
          {periodSplit && (
            <>
              <button
                type="button"
                className={timeRange === 'p1-6' ? 'active' : ''}
                onClick={() => setTimeRange('p1-6')}
              >
                {periodSplit.first}
              </button>
              <button
                type="button"
                className={timeRange === 'p7-13' ? 'active' : ''}
                onClick={() => setTimeRange('p7-13')}
              >
                {periodSplit.second}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="layer-controls">
        {(Object.keys(CORE_LAYER_CONFIG) as CoreLayerKey[]).map((key) => {
          const config = CORE_LAYER_CONFIG[key];
          return (
            <button
              key={key}
              type="button"
              className={`layer-toggle ${activeLayers.has(key) ? 'active' : ''}`}
              onClick={() => toggleLayer(key)}
            >
              <span className="lt-dot" style={{ background: config.color }} />
              {config.label}
            </button>
          );
        })}
        {sportKeys.map((sportKey, index) => (
          <button
            key={sportKey}
            type="button"
            className={`layer-toggle ${activeLayers.has(sportKey) ? 'active' : ''}`}
            onClick={() => toggleLayer(sportKey)}
          >
            <span className="lt-dot" style={{ background: getSportLayerColor(sportKey, index) }} />
            {sportKey}
          </button>
        ))}
      </div>

      <div className="hero-chart-container">
        {loading ? (
          <div className="hero-chart-empty">Loading chart…</div>
        ) : chartData.length === 0 ? (
          <div className="hero-chart-empty">No training data for this season</div>
        ) : activeLayers.size === 0 ? (
          <div className="hero-chart-empty">Select a layer to view the chart</div>
        ) : (
          <StableResponsiveContainer width="100%" height={354}>
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: rightMargin, left: leftMargin, bottom: 0 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" yAxisId={gridAxisId} />
              <XAxis
                dataKey="weekLabel"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              {showDistance && (
                <YAxis
                  yAxisId="distance"
                  orientation="left"
                  domain={distanceDomain}
                  width={distanceAxisWidth}
                  tick={{ fontSize: 10, fill: '#60A5FA' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value} ${distanceUnit}`}
                />
              )}
              {showDurationAxis && (
                <YAxis
                  yAxisId="duration"
                  orientation="left"
                  domain={durationDomain}
                  width={durationAxisWidth}
                  dx={showDistance ? -distanceAxisWidth : 0}
                  tick={{ fontSize: 10, fill: '#374151' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatChartHoursTick}
                />
              )}
              {showCumulative && (
                <YAxis
                  yAxisId="cumulative"
                  orientation="right"
                  domain={cumulativeDomain}
                  width={cumulativeAxisWidth}
                  tick={{ fontSize: 10, fill: '#8B5CF6' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatChartHoursTick}
                />
              )}
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatTooltipValue(getLayerKeyFromName(name), value),
                  name,
                ]}
                labelFormatter={(_, payload) => {
                  const row = payload?.[0]?.payload as { weekIndex?: number; weekLabel?: string } | undefined;
                  if (row?.weekIndex != null && row?.weekLabel) {
                    return `Week ${row.weekIndex} · ${row.weekLabel}`;
                  }
                  return row?.weekLabel ? `Week of ${row.weekLabel}` : '';
                }}
              />
              {renderSeries()}
            </ComposedChart>
          </StableResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default SeasonOverviewChart;
